import createHash from 'create-hash/browser';
import { format } from 'date-fns';
import env from './env';
import URI from 'urijs';
import ImgixClient from 'imgix-core-js';
import _ from 'underscore';
import Backbone from '@polleverywhere/backbone';
import * as _str from 'underscore.string';
import { adjectives, nouns } from './util_words';
import DOMPurify from 'dompurify';
/*
 * Removes HTML tags unless they are in the given whitelist.
 *    stripTags "foo <bar> baz"          # foo  baz"
 *    stripTags "foo <bar> baz", "bar"   # foo <bar> baz"
 *    stripTags "foo <bar> baz", "baz"   # foo  baz"
 * WARNING: Using a regex to sanitize user input can never be perfect.
 *          See: http://stackoverflow.com/a/535022/703108
 *          This is a defense-in-depth measure to make an attack harder in
 *          the event that our server-side sanitization has a bug.
 * Explicitly list what can be whitelisted to help prevent something unsafe
 * from being passed in to the whitelist.
 */

var WHITELISTABLE = ['strong', 'em', 'br'];

var stripTags = function stripTags(str, whitelist) {
  whitelist = _.intersection(whitelist, WHITELISTABLE);
  return DOMPurify.sanitize(str, {
    ALLOWED_TAGS: whitelist,
    ALLOWED_ATTR: ['']
  });
}; // Exports


export var str = _str;
export var IS_NUMBER_RE = /^\d+$/; // check if a string only contains digits

export var IMAGE_REGEX = /^https?:\/\/.+\/[^/?]+\.(?:png|jpe?g|gif|image)[^,]*$/i;
export var UPLOADED_IMAGE_REGEX = /^https?:\/\/s3\.amazonaws\.com\/polleverywhere-images\//i;
export var LATEX_REGEX = /^latex:(.+)/i;
export var EMAIL_REGEXP = /([^@\s]+)@((?:[-a-z0-9]+\.)+[a-z]{2,})/i;
/*
 * Clickable Region Syntax: region: x%,y%|w%xh%#name
 * e.g: region: 10%,15%|25%x50%#persons face
 */

export var CLICKABLE_REGION_REGEX = /^region:\s*(\d+\.?\d*)%,(\d+\.?\d*)%(\|(\d+\.?\d*)%x(\d+\.?\d*)%)?(?:\#(.+))?$/i; // eslint-disable-line no-useless-escape

/*
 * used to render long lists of elements without blocking the UI
 * Originally from http://www.nczonline.net/blog/2009/08/11/timed-array-processing-in-javascript/
 * Updated version from: https://gist.github.com/pc035860/8158249
 * Modified by Steel
 * Usage:
 * timedChunk itemsToProcess, (item) =>
 *   @doStuff(item)
 * , @, (itemsToProcess) =>
 *   @doAfterward(itemsToProcess)
 */

export var timedChunk = function timedChunk(items, process, context, callback) {
  var todo = items.concat(); // create a clone of the original

  var i = 0; // index counter

  var dfd = Backbone.$.Deferred();

  var routine = function routine() {
    Backbone.$.when().then(function () {
      return process.call(context, todo.shift(), i++);
    }).then(function () {
      if (todo.length > 0) {
        setImmediate(routine);
      } else {
        if (typeof callback !== 'undefined') {
          callback(items);
        }

        dfd.resolve(items);
      }
    });
  };

  setImmediate(routine);
  return dfd.promise();
};
export var mod = function mod(number, base) {
  var result = number % base;

  if (result < 0 && base > 0) {
    result += base;
  }

  return result;
}; // expects an array with only numbers as entries

export var sum = function sum(arr) {
  /*
   * _.compact removes falsy values, including 0
   * since we're summing values that's fine
   */
  var compacted = _.compact(arr);

  return _.inject(compacted, function (memo, item) {
    memo += item;
    return memo;
  }, 0);
};
/**
 * Capitalizes the specified string. If the string has multiple words to be
 * capitalized, specifying a word boundary delimiter will capitalize each word.
 *
 * @example
 * capitalize('hello dog day') => 'Hello dog'
 * capitalize('hello dog day', { delimit: ' ' }) => 'Hello Dog Day'
 *
 * @param {string} str Contains the word(s) to be capitalized.
 * @param {Object} [options={}]
 * @param {Object} [options.delimit=''] Specify a delimiter to capitalize all
 *    words contained in the string value.
 * @return {string}
 */

export var capitalize = function capitalize(str, options) {
  if (options === void 0) {
    options = {};
  }

  options = _.defaults(options, {
    delimit: ''
  });

  if (options.delimit.length) {
    var words = str.split(options.delimit).map(function (word) {
      return capitalize(word);
    });
    str = words.join(options.delimit);
  } else {
    str = str.charAt(0).toUpperCase() + str.slice(1);
  }

  return str;
};
export var commaFormat = function commaFormat(val) {
  if (val == null) {
    val = '';
  }

  return val.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};
/*
 * simple visualization for improved CIP region readability
 * takes a value of the form `"59.23%,42.81%"` and converts into
 * a simple image that looks like
 *  __________
 * |     _    |
 * |    |_|   |
 * |          |
 * |          |
 * |__________|
 */

export var visualizeImageRegion = function visualizeImageRegion(regionValue, omitExplanation) {
  if (regionValue == null) {
    regionValue = '';
  }

  if (omitExplanation == null) {
    omitExplanation = false;
  }

  var regionPositions = regionValue.split(',');
  var offsetLeft = regionPositions[0].replace('region: ', '');
  var offsetTop = regionPositions[1];

  if (!offsetLeft || !offsetTop) {
    return '';
  }

  var $el = Backbone.$('<div>', {
    "class": 'cip-viz'
  });

  if (!omitExplanation) {
    $el.append('<div class="cip-viz--explanation">Clicked image region</div>');
  }

  var $bg = Backbone.$('<div>', {
    "class": 'cip-viz--background',
    css: {
      border: '1px solid black',
      width: '50px',
      height: '50px',
      position: 'relative'
    }
  });
  var $region = Backbone.$('<div>', {
    "class": 'cip-viz--region',
    css: {
      width: '15%',
      height: '15%',
      position: 'absolute',
      background: 'black',
      left: offsetLeft,
      top: offsetTop
    }
  });
  $bg.append($region);
  return $el.append($bg);
};
/*
 * @param [String] imageUrl
 * @param [Object] opts
 * @return [String]
 */

export var proxiedImageUrl = function proxiedImageUrl(imageUrl, opts) {
  if (imageUrl == null) {
    imageUrl = '';
  }

  if (opts == null) {
    opts = {};
  }

  imageUrl = imageUrl.toString(); // we won't use imgix_url in all environments

  var imgixUrl = env.get('imgix_url');

  if (imgixUrl) {
    _.defaults(opts, {
      dpr: window.devicePixelRatio
    });

    var imgix = new ImgixClient({
      domain: imgixUrl.host(),
      secureURLToken: env.get('imgix_token')
    });
    return imgix.buildURL(imageUrl, opts);
  } else {
    return imageUrl;
  }
};
export var cleanString = function cleanString(str, opts) {
  if (opts === void 0) {
    opts = {};
  }

  if (!str) {
    return '';
  }

  var ret = _str.unescapeHTML(str);

  ret = stripTags(ret, opts.whitelist);
  ret = _str.clean(ret);
  return ret;
};
export var deepDefaults = function deepDefaults(target) {
  for (var _len = arguments.length, sources = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
    sources[_key - 1] = arguments[_key];
  }

  if (sources.length > 1) {
    sources.forEach(function (source) {
      return deepDefaults(target, source);
    });
  } else {
    var obj = sources[0];

    _.defaults(target, obj);

    _.each(obj, function (value, key) {
      if (_.isObject(value)) {
        deepDefaults(target[key], obj[key]);
      } else if (_.isNull(target[key])) {
        target[key] = obj[key];
      }
    });
  }

  return target;
};
export var params = function params(url) {
  var params = {};

  try {
    params = URI(URI.decode(url)).query(true);
  } catch (e) {
    console.error(url + " is invalid.");
  }
  /*
   * NOTE: This is to preserve the expected params object with values of a
   *       number to actually be a number and not a string representation of a
   *       number.
   */


  for (var key in params) {
    var value = params[key];

    if (IS_NUMBER_RE.test(value)) {
      params[key] = parseInt(value);
    }
  }

  return params;
};
export var paramsFromLocation = function paramsFromLocation() {
  return params(window.location.href);
};
export var popRootKey = function popRootKey(json) {
  if (json != null && hasRootKey(json)) {
    return _.values(json)[0];
  } else {
    return json;
  }
};
export var hasRootKey = function hasRootKey(json) {
  return _.values(json).length === 1;
};
export var popRootKeys = function popRootKeys(arr) {
  /*
   * We're expecting an array here
   * Lets wrap it in one if it isn't
   */
  return _.map([].concat(arr), function (json) {
    return popRootKey(json);
  });
};
export var rootKey = function rootKey(root, attrs) {
  var json = {};
  json[root] = attrs;
  return json;
};
export var modelType = function modelType(json) {
  /*
   * models might be returned as:
   * - {type: 'multiple_choice_polls'}
   * - {multiple_choice_poll: {}}
   * - {poll_type: 'multiple_choice_polls'} (legacy)
   * TODO: clean up serialization in our controllers and make them consistent
   */
  if (hasRootKey(json)) {
    return _.first(_.keys(json));
  } else {
    return json.type || json.poll_type;
  }
};
/**
 * Returns the icon font glyph name to represent the specified activity type.
 * @param {string} type Activity type
 * @return {string} Glyph name
 */

export var activityTypeToIcon = function activityTypeToIcon(type) {
  return {
    clickable_image: 'image',
    competition: 'trophy',
    discourse: 'lightbulb',
    free_text_poll: 'free-text-poll',
    multiple_choice_poll: 'multiple-choice-poll',
    ranking_poll: 'ranking-poll',
    survey: 'survey',
    word_cloud: 'word-cloud'
  }[type] || '';
}; // TODO: Deprecate/remove `pollTypeToIcon` once all consumers are updated.

export var pollTypeToIcon = activityTypeToIcon;
/*
 * Converts timestamp into formatted date
 *
 * @param [String] timestamp Date() can parse (ISO8601 or IETF format, i.e. "2011-09-05T12:30:00-07:00")
 * @return [String] formatted date (i.e. "Sep 5, 2011") or throws error if date
 *  string is invalid.
 */

export var formatDate = function formatDate(time) {
  return format(new Date(time), 'MMM d, yyyy');
};
/*
 * Converts timestamp into formatted date (i.e. )
 *
 * @param [String] timestamp Date() can parse (ISO8601 or IETF format, i.e. "2011-09-05T12:30:00-07:00")
 * @return [String] formatted time (i.e. "Sep 5, 2011 at 12:30PM") or throws error if timestamp is invalid.
 */

export var formatTime = function formatTime(time) {
  return format(new Date(time), 'MMM d, yyyy \'at\' h:mma');
};
export var classify = function classify(type) {
  var lookup = {
    multiple_choice_poll: 'MultipleChoicePoll',
    free_text_poll: 'FreeTextPoll',
    discourse: 'Discourse',
    clickable_image: 'ClickableImage',
    ranking_poll: 'RankingPoll',
    survey: 'Survey',
    competition: 'Competition',
    join_instructions: 'JoinInstructions'
  };
  return lookup[type];
};
export var url = function url(path, options) {
  if (options == null) {
    options = {};
  }

  var uri = URI(path).absoluteTo(env.get('api_url'));

  if (Backbone.$.os != null ? Backbone.$.os.android : undefined) {
    uri.setQuery('cache_buster', new Date().getTime().toString());
  }

  return uri;
};
export var vanityUrl = function vanityUrl(path) {
  var uri;

  if (env.get('root_url').protocol() === 'file') {
    /*
     * uri.js has weird semantics--host and path are switched?-- when it
     * comes to file uris (which we use in the cordova app)
     */
    uri = env.get('root_url').normalize();
    uri.path(uri.path().replace('pollev.com', 'PollEv.com'));
    return "" + uri.path() + path;
  } else {
    uri = env.get('root_url').path(path).normalize();
    uri.host(uri.host().replace('pollev.com', 'PollEv.com'));
    return "" + uri.host() + uri.path();
  }
};
export var singularize = function singularize(type) {
  return type.replace(/s$/, '');
};
export var pluralize = function pluralize(times, word, options) {
  if (options == null) {
    options = {};
  }

  var irregulars = {
    person: 'people',
    Person: 'People',
    "this": 'these',
    This: 'These',
    Activity: 'Activities',
    activity: 'activities',
    Question: 'Questions',
    question: 'questions'
  };
  var pluralized = times === 1 ? word : irregulars[word] || word + "s";

  if (options.includeNumber) {
    return times + " " + pluralized;
  } else {
    return pluralized;
  }
}; // Found here http://forums.shopify.com/categories/2/posts/29259

export var ordinalize = function ordinalize(n) {
  var s = ['th', 'st', 'nd', 'rd'];
  var v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}; // Reject a promise if it hasn't resolved in the specified time

export var timeoutPromise = function timeoutPromise(promise, timeout) {
  return setTimeout(function () {
    if (promise.state() === 'pending') {
      return promise.reject("Timed out after " + timeout + "!");
    }
  }, timeout);
};
export var randomArrayItem = function randomArrayItem(array) {
  return array[_.random(array.length - 1)];
};
export var randomSentence = function randomSentence() {
  var LORUM_IPSUM = [' Lorum', ' Ipsum', ' Dolor', ' Sit', ' Amet', ' Eiusmod'];
  var sentence = '';

  _(_.random(2, 8)).times(function () {
    sentence += randomArrayItem(LORUM_IPSUM);
  });

  return sentence;
};
export var truncate = function truncate(string, length, delimeter) {
  if (delimeter == null) {
    delimeter = ' ';
  }

  var units = string.split(delimeter);
  var output = units.slice(0, length).join(delimeter);

  if (units.length > length) {
    output += '...';
  }

  return output;
};
export var autolink = function autolink(string) {
  return string.replace(FIND_LINKS_REGEXP, function (_, space, protocol, url) {
    var prefix;

    if (protocol == null) {
      protocol = '';
    }

    if (protocol) {
      prefix = protocol;
    } else {
      prefix = 'http://';
    }

    return space + '<a href="' + prefix + url + '" target="_blank">' + protocol + url + '</a>';
  });
};
/*
 * Used for collection comparators
 * returns
 *   -1 if the first val comes before the second val
 *    1 if val2 should come before val1
 *    0 if they are equal
 */

export var compareInt = function compareInt(val1, val2) {
  if (val1 == null) {
    val1 = 0;
  }

  if (val2 == null) {
    val2 = 0;
  }

  if (val1 > val2) {
    return -1;
  } else if (val2 > val1) {
    return 1;
  } else {
    return 0;
  }
};
export var regexSearch = function regexSearch(s) {
  /*
   * Adapted from proposal for adding a RegExp.escape method to the ECMAScript standard
   * https://github.com/benjamingr/RegExp.escape/blob/master/polyfill.js
   */
  s = String(s).replace(/[\\^$*+?.()|[\]{}]/g, '\\$&');
  return new RegExp(s, 'i');
};
/*
 * Calls func every deltaMilliseconds with all arguments passed to it since
 * the last interval.
 * Can be stopped by calling .stop() on the returned object.
 */

export var batchCallFunction = function batchCallFunction(deltaMilliseconds, func) {
  var queue = [];
  var interval = setInterval(function () {
    if (queue.length) {
      func.call(this, queue);
      queue = [];
    }
  }, deltaMilliseconds);

  var batch = function batch() {
    for (var _len2 = arguments.length, args = new Array(_len2), _key2 = 0; _key2 < _len2; _key2++) {
      args[_key2] = arguments[_key2];
    }

    return queue.push(args);
  };

  batch.stop = function () {
    return clearInterval(interval);
  };

  return batch;
};
export var FIND_LINKS_REGEXP = new RegExp(
/*
 * Modified to be more relaxed/simple from https://gist.github.com/dperini/729294/ad7c8d5b4834f2de4c42e24d4a09b48c202da155
 * start of string or after whitespace
 */
'(^|[\\s\\n\\r])' + // not an attribute
"(?![a-z]+=['\"])" + // protocol identifier
'(https?://)?' + // start url matching group
'(' + '(?:' + // IPv4 address
'(?:(?:\\d{1,3}\\.){3}\\d{1,3})' + '|' + // IPv6 RegEx (very basic) - full version at http://stackoverflow.com/a/17871737/273668
'\\[([0-9a-fA-F]{1,4}::?){1,7}[0-9a-fA-F]{1,4}\\]' + '|' + // host name
"(?:xn--[a-z0-9\\-]{1,59}|(?:(?:[a-z\\u00a1-\\uffff0-9]+-?){0,62}[a-z\\u00a1-\\uffff0-9]{1,63}))" + // domain name
"(?:\\.(?:xn--[a-z0-9\\-]{1,59}|(?:[a-z\\u00a1-\\uffff0-9]+-?){0,62}[a-z\\u00a1-\\uffff0-9]{1,63}))*" + // TLD identifier
"(?:\\.(?:xn--[a-z0-9\\-]{1,59}|(?:[a-z\\u00a1-\\uffff]{2,63})))" + ')' + // port number
'(?::\\d{2,5})?' + // resource path
'(?:/[^\\s]*)?' + // end url matching group
')', 'ig');
export var inDOM = function inDOM(element) {
  var dfd = Backbone.$.Deferred(); // If it's already in DOM, resolve right away

  if (Backbone.$.contains(document.body, element)) {
    dfd.resolve(element);
    return dfd;
  }

  var observer = new window.MutationObserver(function (mutations) {
    if (mutations === void 0) {
      mutations = [];
    }

    mutations.find(function (record) {
      var found = Array.from(record.addedNodes).find(function (node) {
        // if the node is the element, we've found a match
        if (node === element) {
          return true;
        } // otherwise check if element is in this node tree


        return Backbone.$.contains(node, element);
      });

      if (found) {
        dfd.resolve(element);
        return true;
      }
    });
  });
  observer.observe(document.body, {
    childList: true,
    subtree: true
  }); // Disconnect after resolved

  dfd.always(function () {
    observer.disconnect();
  });
  return dfd;
};
/**
 * Generates a one-way hash based on the specified key value provided and
 * returns a multi-word name in the form of `[[ADJECTIVE] ][NOUN]". When more
 * than two words are generated, the last word is always a noun with all leading
 * words being adjectives.
 *
 * @example
 * generateHashName(1) => "bowl"
 * generateHashName(1, { wordCount: 3 }) => "gutsy proud freeze"
 *
 * @param {string|number} key Identifier by which to generate the hash from.
 * @param {Object} [options={}] Additional options.
 * @param {number} [options.wordCount=1] Number of words to generate in the name.
 * @param {boolean} [options.fun=true] Whether to use fun or boring names
 * @return {string}
 */

export var generateHashName = function generateHashName(key, options) {
  if (options === void 0) {
    options = {};
  }

  options = _.defaults(options, {
    wordCount: 1,
    fun: true
  });
  var hash = createHash('sha256');
  hash.update(key.toString());
  var hexHash = hash.digest('hex');
  var decimalHash = parseInt(hexHash, 16);

  if (!options.fun) {
    return "guest" + decimalHash % 1000;
  }

  var wordListCount = adjectives.length + nouns.length;
  var wordIndex = decimalHash % Math.pow(wordListCount, options.wordCount);
  var words = []; // Use adjectives to fill up the word count.

  var adjIndex = wordIndex;

  for (var n = 0; n < options.wordCount - 1; n++) {
    words.push("" + adjectives[adjIndex % adjectives.length]);
    adjIndex = Math.floor(adjIndex / adjectives.length);
  } // Then always end with a noun.


  words.push(nouns[wordIndex % nouns.length]);
  return words.join(' ');
};
/**
 * Normalizes a failed Backbone request to the backend and returns the error
 * object within, which contains the following keys:
 *
 *    type: 'ErrorType',
 *    message: 'Human readable formatted message of all errors',
 *    detail: {
 *      // List of keys as namespaces where the errors occurred.
 *      'poll.base': [
 *        'reason 1',
 *        'reason 2'
 *      ]
 *    }
 *
 * If the failed response is not a JSON object, wraps the plain text response
 * inside a mock error object before returning. This lets consumers normalize a
 * failed response from backend and work with it in a consistent way.
 * @param {jqXHR} $xhr
 * @return {Object}
 */

export function normalizeXhrError(error) {
  if (error.graphQLErrors) {
    return {
      type: 'GraphQLError',
      message: error.message,
      detail: error.graphQLErrors
    };
  } else {
    return error.responseJSON ? error.responseJSON.error : {
      type: 'UnknownError',
      message: error.responseText,
      detail: {}
    };
  }
}